import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import * as yup from 'yup';

import { NestedFields } from '@/components/molecules/NestedFields';
import { Field } from '@/types/fields';
import { changeObserver } from '@/utility/changeObserver';
import { formLikeElementToObject } from '@/utility/formLikeElementToObject';

interface Props<T> {
  readonly name?: keyof T | string;
  readonly title?: string;
  readonly fields: ReadonlyArray<Field<T>>;
  readonly columnsCount: number;
  readonly errors?: Record<string, yup.ValidationError>;
  readonly required?: boolean;
  readonly defaultValue?: any;
}

export function CompositeFormFieldElement<T>(props: Props<T>): React.ReactElement {
  const { name, fields, title, errors, columnsCount, defaultValue } = props;
  const [value, setValue] = React.useState<string>(JSON.stringify(defaultValue));

  const ref = React.useRef<HTMLFieldSetElement>(null);

  const changeHandler = React.useCallback(
    (fieldset: HTMLFieldSetElement): void => {
      const newItem = formLikeElementToObject(fieldset, fields);
      setValue(JSON.stringify(newItem));
    },
    [fields],
  );

  React.useEffect((): VoidFunction | void => {
    const { current: fieldset } = ref;
    if (!fieldset) {
      return;
    }

    return changeObserver(fieldset, fields, changeHandler);
  }, [changeHandler, fields]);

  return (
    <Box>
      <input name={String(name)} value={value} type="hidden" />

      {title && (
        <Box mb={2}>
          <Typography variant="subtitle1">{title}</Typography>
        </Box>
      )}

      <Box sx={styles.fieldset} component="fieldset" ref={ref}>
        <NestedFields
          fields={fields}
          errors={errors}
          columnsCount={columnsCount}
          defaultValue={defaultValue}
        />
      </Box>
    </Box>
  );
}

const styles: Record<string, SystemStyleObject> = {
  fieldset: {
    border: 'none',
    padding: 0,
    margin: 0,
  },
};
